import { Injectable, inject } from '@angular/core';
import { forEach, flatten, isEmpty, isObject, sortBy, toArray } from 'lodash';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';


export interface IReleaseNotesResponse {
  [date: string]: {
    topic: string;
    internalid: string;
    heading: string;
    _time: number;
    category?: string;
    product?: string;
    _new: {
      entry_for_new: string;
    }[];
    _improved: {
      entry_for_improved: string;
    }[];
    _fixed: {
      entry_for_fixed: string;
    }[];
  }[]
}


export interface INews2 {
  type: string;
  value: string;
  attributes?: Record<string, any>;
  children?: INews2[]
}



@Injectable({
  providedIn: 'root'
})
export class ReleaseNotesService {
  private cachedResponse?: Partial<IReleaseNotesResponse>;
  private httpClient = inject(HttpClient);



  private fetchOrSubscribe() {
    if (this.cachedResponse !== undefined) {
      return of(this.cachedResponse);
    }

    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/x-www-form-urlencoded');

    return this.httpClient.get<Partial<IReleaseNotesResponse>>(`${location.protocol}//${location.host}/igstatic/releasenotes/notes.json`, { headers: headers }).pipe(
      tap(response => this.cachedResponse = response)
    );
  }


  /**
   * fetches the releasenotes from gos3 and performs the grouuping
   * @returns
   */
  fetch(): Observable<INews2[]> {
    return new Observable((_observer) => {
      this.fetchOrSubscribe().pipe(
        catchError((_e: HttpErrorResponse) => {
          _observer.error(_e);
          throw 'Request error ' + _e.message;
        }),
      ).subscribe(_res => {
        if (!isEmpty(_res) && isObject(_res)) {
          let releaseNotes = flatten(toArray(_res));
          releaseNotes = sortBy(releaseNotes, _data => _data!._time * -1);

          const releaseNotesOrdered: INews2[] = [];


          const getNewsPointer = (type: string, value: string, news = releaseNotesOrdered) => {
            let found: INews2 | undefined;

            forEach(news, (entry, key) => {
              if (entry.type === type && entry.value === value) {
                found = news[key];
                return false;
              }
              if (entry.children?.length) {
                found = getNewsPointer(type, value, news[key].children);
                if (found) {
                  return false;
                }
              }
            });

            return found;
          }

          forEach(releaseNotes, notes => {
            if (!!notes!.category && !!notes!.product) {
              let headingPointer = getNewsPointer('heading', notes!.heading, releaseNotesOrdered);
              if (headingPointer === undefined) {
                releaseNotesOrdered.push({
                  type: 'heading',
                  value: notes!.heading,
                  attributes: {
                    time: notes!._time,
                  },
                  children: []
                });
                headingPointer = getNewsPointer('heading', notes!.heading, releaseNotesOrdered);
                headingPointer = headingPointer!;
              }


              let categoryPointer = getNewsPointer('category', notes!.category, headingPointer.children);
              if (categoryPointer === undefined) {
                headingPointer.children!.push({
                  type: 'category',
                  value: notes!.category,
                  children: []
                });
                categoryPointer = getNewsPointer('category', notes!.category, headingPointer.children);
                categoryPointer = categoryPointer!;
              }

              let productPointer = getNewsPointer('product', notes!.product, categoryPointer.children);
              if (productPointer === undefined) {
                categoryPointer.children!.push({
                  type: 'product',
                  value: notes!.product,
                  children: []
                });

                productPointer = getNewsPointer('product', notes!.product, categoryPointer.children);
                productPointer = productPointer!;
              }

              if (notes!._new?.length) {
                let releaseNoteTypePointerNew = getNewsPointer('release_note_type', 'new', productPointer.children);
                if (releaseNoteTypePointerNew === undefined) {
                  productPointer.children!.push({
                    type: 'release_note_type',
                    value: 'new',
                    children: []
                  });
                  releaseNoteTypePointerNew = getNewsPointer('release_note_type', 'new', productPointer.children);
                  releaseNoteTypePointerNew = releaseNoteTypePointerNew!;
                }

                notes!._new.forEach(noteEntry => {
                  releaseNoteTypePointerNew!.children!.push({
                    type: 'release_note',
                    value: noteEntry.entry_for_new
                  });
                });
              }

              if (notes!._improved?.length) {
                let releaseNoteTypePointerImproved = getNewsPointer('release_note_type', 'improved', productPointer.children);
                if (releaseNoteTypePointerImproved === undefined) {
                  productPointer.children!.push({
                    type: 'release_note_type',
                    value: 'improved',
                    children: []
                  });
                  releaseNoteTypePointerImproved = getNewsPointer('release_note_type', 'improved', productPointer.children);
                  releaseNoteTypePointerImproved = releaseNoteTypePointerImproved!;
                }

                notes!._improved.forEach(noteEntry => {
                  releaseNoteTypePointerImproved!.children!.push({
                    type: 'release_note',
                    value: noteEntry.entry_for_improved
                  });
                });
              }

              if (notes!._fixed?.length) {
                let releaseNoteTypePointerFixed = getNewsPointer('release_note_type', 'fixed', productPointer.children);
                if (releaseNoteTypePointerFixed === undefined) {
                  productPointer.children!.push({
                    type: 'release_note_type',
                    value: 'fixed',
                    children: []
                  });
                  releaseNoteTypePointerFixed = getNewsPointer('release_note_type', 'fixed', productPointer.children);
                  releaseNoteTypePointerFixed = releaseNoteTypePointerFixed!;
                }

                notes!._fixed.forEach(noteEntry => {
                  releaseNoteTypePointerFixed!.children!.push({
                    type: 'release_note',
                    value: noteEntry.entry_for_fixed
                  });
                });
              }
            }
          });

          _observer.next(releaseNotesOrdered);
        } else {
          _observer.next([]);
        }
        _observer.complete();


      });
    });
  }

}
